import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { loadTrustpilotCollector } from '@/lib/trustpilotCollector';
import type { TrustpilotCollectorElement } from '@/types/trustpilot';
import { trustpilotService, type TrustpilotReviewRequest } from '../api/trustpilotService';
import { getAppLanguage } from '@/i18n/languages';

/** Identifiants publics du collector Trustpilot, communs à tous les environnements. */
const BUSINESS_UNIT_ID = '699ec1e58652b9a8ffe6fac3';
const TEMPLATE_ID = '53ch7c1d9be8a02f64d31e79';

interface TrustpilotReviewDialogProps {
  /** Demande active renvoyée par le back : le montage de la modale vaut décision de solliciter. */
  request: TrustpilotReviewRequest;
}

export const TrustpilotReviewDialog = ({ request }: TrustpilotReviewDialogProps) => {
  const { t, i18n } = useTranslation('analytics');

  const [isOpen, setIsOpen] = useState(false);
  const collectorRef = useRef<TrustpilotCollectorElement | null>(null);

  useEffect(() => {
    let isStillMounted = true;

    const prepare = async () => {
      try {
        // Le widget doit être défini avant l'ouverture : sinon un bloqueur laisse un voile gris vide.
        await loadTrustpilotCollector();
        if (!isStillMounted) return;

        setIsOpen(true);
        void trustpilotService.markDisplayed();
      } catch {
        // Une sollicitation manquée ne doit jamais dégrader la page Analytics.
      }
    };

    void prepare();

    return () => {
      isStillMounted = false;
    };
  }, [request.id]);

  /** Le portail Radix ne monte son contenu qu'au commit suivant l'ouverture : un effet dépendant
   *  de `isOpen` lit encore une référence vide et n'est jamais rejoué. Le ref callback, lui, suit
   *  le montage réel. `onReady` est un verrou côté widget, l'affecter tard le rappelle aussitôt. */
  const attachCollector = useCallback((element: TrustpilotCollectorElement | null) => {
    collectorRef.current = element;
    if (!element) return;

    element.onReady = () => {
      void (async () => {
        try {
          const payload = await trustpilotService.getPayload();
          if (collectorRef.current !== element) return;
          element.dispatchEvent(new CustomEvent('customerDataResponse', { detail: payload }));
        } catch {
          // Sans charge utile signée, le widget reste sur son écran d'invitation.
        }
      })();
    };
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) return;

    void (async () => {
      try {
        await trustpilotService.dismiss();
      } catch {
        // Refus non enregistré : la demande restera active, on resollicitera au prochain live.
      }
    })();
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[624px] p-6" aria-describedby={undefined}>
        <DialogTitle className="sr-only">{t('trustpilotReview.title')}</DialogTitle>
        <trustbox-verified-review-collector
          ref={attachCollector}
          className="trustpilot-widget"
          style-size="M"
          style-width="100%"
          locale={getAppLanguage(i18n.language).locale}
          template-id={TEMPLATE_ID}
          businessunit-id={BUSINESS_UNIT_ID}
          border-type="shadow"
          has-animated-stars="true"
        />
      </DialogContent>
    </Dialog>
  );
};
